import { baseApi } from "../../../../core/data/rtk/baseQuery";
import { ApiResponseType } from "../../../../core/data/rtk/apiResponseType";
import { MarksResponseType } from "../types/marksResponse";
import { MarksPayloadType } from "../types/marksRequest";

interface BulkMarksPayload {
  marks: MarksPayloadType[];
}

interface BulkUpdatePayload {
  marks: Array<{
    id: number;
    student_id: number;
    course_id: number;
    stage_id: number;
    session_id: number;
    obtained: number;
    max?: number;
    min?: number;
  }>;
}

export const assignMarksApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllMarks: builder.query<
      ApiResponseType<{
        items: MarksResponseType[];
        pageSize: number;
        currentPage: number;
        totalItems: number;
        totalPages: number;
      }>,
      {
        size?: number;
        page?: number;
        session_id?: string;
        course_id?: string;
        stage_id?: string;
      }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.size) queryParams.append("size", params.size.toString());
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.session_id)
          queryParams.append("session_id", params.session_id);
        if (params?.course_id)
          queryParams.append("course_id", params.course_id);
        if (params?.stage_id) queryParams.append("stage_id", params.stage_id);

        return `marks/get-all?${queryParams.toString()}`;
      },
      providesTags: ["Marks"],
    }),

    assignMarks: builder.mutation<
      ApiResponseType<MarksResponseType>,
      MarksPayloadType
    >({
      query: (payload) => ({
        url: "marks/save",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Marks", "Students"],
    }),

    assignBulkMarks: builder.mutation<
      ApiResponseType<MarksResponseType[]>,
      BulkMarksPayload
    >({
      query: (payload) => ({
        url: "marks/bulk/save",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Marks", "Students"],
    }),

    updateMarks: builder.mutation<
      ApiResponseType<MarksResponseType>,
      { id: number; payload: Partial<MarksPayloadType> }
    >({
      query: ({ id, payload }) => ({
        url: `marks/update/${id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Marks", "Students"],
    }),

    updateBulkMarks: builder.mutation<
      ApiResponseType<MarksResponseType[]>,
      BulkUpdatePayload
    >({
      query: (payload) => ({
        url: "marks/bulk/update",
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Marks", "Students"],
    }),

    getMarksByStudent: builder.query<
      ApiResponseType<{ marks: MarksResponseType[] }>,
      { student_id: string }
    >({
      query: ({ student_id }) => `marks/get-by-student/${student_id}`,
      providesTags: ["Marks"],
    }),
  }),
});

export const {
  useGetAllMarksQuery,
  useAssignMarksMutation,
  useAssignBulkMarksMutation,
  useUpdateMarksMutation,
  useUpdateBulkMarksMutation,
  useGetMarksByStudentQuery,
} = assignMarksApi;
